/**
 * MQTT 토픽 정의 — 단일 진실 소스
 *
 * 네이밍 규칙:
 *   센서 번들:   grosync/{deviceId}/data    (장비당 단일 JSON 텔레메트리 — 모든 센서값)
 *   제어 명령:   grosync/{deviceId}/command
 *   상태 보고:   grosync/{deviceId}/status
 *
 * 모든 MQTT 토픽은 반드시 이 파일에서만 정의한다.
 * 다른 패키지에서 토픽 문자열을 하드코딩하지 않는다.
 */
const ROOT = 'grosync';
/** 센서 번들 토픽의 마지막 세그먼트 (grosync/{deviceId}/data) */
const BUNDLE_SEGMENT = 'data';
/** RS485 Modbus 하위 노드 세그먼트 (grosync/{masterId}/node/{slaveAddr}/...) */
const NODE_SEGMENT = 'node';
const ACTUATOR_SEGMENT = 'actuator';
export const TOPICS = {
    // ---------------------------------------------------------------------------
    // 센서 번들 (Publish: 디바이스 → 브로커, Subscribe: mqtt-colct)
    // 장비당 단일 JSON으로 모든 센서값을 묶어 발행한다.
    // ---------------------------------------------------------------------------
    /** 특정 디바이스의 센서 번들: grosync/{deviceId}/data */
    SENSOR_BUNDLE: (deviceId) => `${ROOT}/${deviceId}/${BUNDLE_SEGMENT}`,
    /** 모든 디바이스 센서 번들 구독용 와일드카드: grosync/+/data */
    SENSOR_BUNDLE_ALL: `${ROOT}/+/${BUNDLE_SEGMENT}`,
    // ---------------------------------------------------------------------------
    // RS485 Modbus 하위 노드 (KS X 3267) — 마스터가 게이트웨이로 중계
    //   센서 노드 번들:   grosync/{masterId}/node/{slaveAddr}/data
    //   구동기 노드 상태: grosync/{masterId}/node/{slaveAddr}/actuator
    // ---------------------------------------------------------------------------
    /** 하위 노드 센서 번들: grosync/{masterId}/node/{slaveAddr}/data */
    NODE_SENSOR_BUNDLE: (masterId, slaveAddr) => `${ROOT}/${masterId}/${NODE_SEGMENT}/${slaveAddr}/${BUNDLE_SEGMENT}`,
    /** 하위 노드 센서 번들 구독용 와일드카드: grosync/+/node/+/data */
    NODE_SENSOR_BUNDLE_ALL: `${ROOT}/+/${NODE_SEGMENT}/+/${BUNDLE_SEGMENT}`,
    /** 하위 구동기 노드 상태: grosync/{masterId}/node/{slaveAddr}/actuator */
    NODE_ACTUATOR_STATUS: (masterId, slaveAddr) => `${ROOT}/${masterId}/${NODE_SEGMENT}/${slaveAddr}/${ACTUATOR_SEGMENT}`,
    /** 하위 구동기 노드 상태 구독용 와일드카드: grosync/+/node/+/actuator */
    NODE_ACTUATOR_STATUS_ALL: `${ROOT}/+/${NODE_SEGMENT}/+/${ACTUATOR_SEGMENT}`,
    // ---------------------------------------------------------------------------
    // 제어 명령 (Publish: 서버 → 브로커, Subscribe: 디바이스)
    // ---------------------------------------------------------------------------
    /** 특정 디바이스에 제어 명령 전송: grosync/{deviceId}/command */
    COMMAND: (deviceId) => `${ROOT}/${deviceId}/command`,
    /** 특정 디바이스 명령 실행 결과(ACK): grosync/{deviceId}/command/result */
    COMMAND_RESULT: (deviceId) => `${ROOT}/${deviceId}/command/result`,
    /** 모든 디바이스 명령 결과 구독용 와일드카드: grosync/+/command/result */
    COMMAND_RESULT_ALL: `${ROOT}/+/command/result`,
    // ---------------------------------------------------------------------------
    // 상태 보고 (Publish: 디바이스 → 브로커, Subscribe: mqtt-colct)
    // ---------------------------------------------------------------------------
    /** 특정 디바이스 상태 보고: grosync/{deviceId}/status */
    DEVICE_STATUS: (deviceId) => `${ROOT}/${deviceId}/status`,
    /** 모든 디바이스 상태 구독용 와일드카드: grosync/+/status */
    DEVICE_STATUS_ALL: `${ROOT}/+/status`,
    // ---------------------------------------------------------------------------
    // Dead Letter Queue (mqtt-colct 내부 사용)
    // ---------------------------------------------------------------------------
    /**
     * 검증 실패 메시지 DLQ 토픽: $dlq/{originalTopic}
     *
     * `$` 로 시작하는 토픽은 MQTT 스펙상 일반 와일드카드(+, #) 구독에 매칭되지 않으므로
     * 서비스 자신의 grosync/+/data 구독에 재수신되지 않아 무한 루프가 발생하지 않는다.
     */
    DEAD_LETTER: (originalTopic) => `$dlq/${originalTopic}`,
};
// ---------------------------------------------------------------------------
// 토픽 파싱 헬퍼
// ---------------------------------------------------------------------------
/**
 * 센서 번들 토픽(grosync/{deviceId}/data)에서 deviceId를 파싱한다.
 * 번들 토픽이 아니면 null 반환.
 * e.g. 'grosync/abc123/data' → 'abc123'
 */
export function parseBundleTopic(topic) {
    const parts = topic.split('/');
    if (parts.length !== 3 || parts[0] !== ROOT || parts[2] !== BUNDLE_SEGMENT)
        return null;
    return parts[1] || null;
}
/**
 * 상태/명령 토픽에서 deviceId를 파싱한다.
 * e.g. 'grosync/abc123/status' → 'abc123'
 */
export function parseDeviceIdFromTopic(topic) {
    const parts = topic.split('/');
    if (parts.length !== 3 || parts[0] !== ROOT)
        return null;
    return parts[1] ?? null;
}
function makeNodeRef(masterId, addrStr) {
    if (!masterId || !addrStr)
        return null;
    const slaveAddr = Number(addrStr);
    // KS X 3267 슬레이브 주소 범위 1~247
    if (!Number.isInteger(slaveAddr) || slaveAddr < 1 || slaveAddr > 247)
        return null;
    return { masterId, slaveAddr };
}
/**
 * 하위 노드 센서 번들 토픽에서 {masterId, slaveAddr} 파싱.
 * 노드 번들 토픽이 아니면 null.
 * e.g. 'grosync/master-01/node/5/data' → { masterId: 'master-01', slaveAddr: 5 }
 */
export function parseNodeBundleTopic(topic) {
    const p = topic.split('/');
    if (p.length !== 5 || p[0] !== ROOT || p[2] !== NODE_SEGMENT || p[4] !== BUNDLE_SEGMENT)
        return null;
    return makeNodeRef(p[1], p[3]);
}
/**
 * 하위 구동기 노드 상태 토픽에서 {masterId, slaveAddr} 파싱.
 * e.g. 'grosync/master-01/node/20/actuator' → { masterId: 'master-01', slaveAddr: 20 }
 */
export function parseNodeActuatorTopic(topic) {
    const p = topic.split('/');
    if (p.length !== 5 || p[0] !== ROOT || p[2] !== NODE_SEGMENT || p[4] !== ACTUATOR_SEGMENT)
        return null;
    return makeNodeRef(p[1], p[3]);
}
//# sourceMappingURL=topics.js.map